import React, { useState, useEffect } from 'react';
import Layout from '@/components/Layout';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ArrowRight, Send, AlertTriangle, Zap, Settings } from 'lucide-react';
import { toast } from 'sonner';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend } from 'recharts';

import { BACKEND_URL, API } from '../config';

const ArbitrageManager = () => {
  const [opportunities, setOpportunities] = useState([]);
  const [exchanges, setExchanges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showTransferDialog, setShowTransferDialog] = useState(false);
  const [selectedOpp, setSelectedOpp] = useState(null);
  const [transferForm, setTransferForm] = useState({
    from_exchange: '',
    to_exchange: '',
    currency: '',
    amount: ''
  });
  const [transferring, setTransferring] = useState(false);
  const [paperMode, setPaperMode] = useState(true);
  const [investmentActive, setInvestmentActive] = useState(false);
  const [wsStatus, setWsStatus] = useState('conectando');
  const [autoConfig, setAutoConfig] = useState({ activo: false, umbral_pct: 0.05, capital_max_usd: 100, paper_trading: true, modo_unilateral: false });
  const [showAutoConfig, setShowAutoConfig] = useState(false);
  const [historial, setHistorial] = useState([]);
  const [arbFull, setArbFull] = useState([]);
  const [paperBal, setPaperBal] = useState(null);

  useEffect(() => {
    loadData();
    const interval = setInterval(loadOpportunities, 3000);
    return () => clearInterval(interval);
  }, []);

  const loadData = async () => {
    try {
      // Cargar exchanges, config, historial e inversión en paralelo — respuesta rápida
      const [exchangesRes, cfgRes, histRes, pbRes, payRes, pfRes] = await Promise.all([
        axios.get(`${API}/exchanges/keys`, { withCredentials: true, timeout: 8000 }).catch(() => ({ data: { keys: [] } })),
        axios.get(`${API}/arbitrage/auto/config`, { withCredentials: true, timeout: 5000 }).catch(() => ({ data: {} })),
        axios.get(`${API}/arbitrage/historial?limit=200`, { withCredentials: true, timeout: 8000 }).catch(() => ({ data: { historial: [] } })),
        axios.get(`${API}/paper/balance`, { withCredentials: true, timeout: 5000 }).catch(() => null),
        axios.get(`${API}/payment/info`, { withCredentials: true, timeout: 5000 }).catch(() => null),
        axios.get(`${API}/portfolio/trades-full`, { withCredentials: true, timeout: 8000 }).catch(() => ({ data: { trades: [] } })),
      ]);
      setExchanges(exchangesRes.data.keys || []);
      if (cfgRes.data.umbral_pct) setAutoConfig(cfgRes.data);
      setHistorial(histRes.data.historial || []);
      // Totales y gráfico de arbitraje SIN el límite de 200 que sí tiene la
      // tabla de abajo (ese límite hacía que "Arbitraje Paper/Real" y la
      // curva de equity pudieran mostrar menos de lo real).
      setArbFull((pfRes.data.trades || [])
        .filter(t => t.fuente === 'Arbitraje')
        .map(t => ({ ganancia_usd: t.pnl, timestamp: t.ts, paper_trading: t.paper_trading })));
      if (pbRes) setPaperBal(pbRes.data);
      const inv = payRes?.data?.investment_active ?? false;
      setInvestmentActive(inv);
      setPaperMode(!inv);
      // Mostrar la página inmediatamente — las oportunidades llegan después
      setLoading(false);
      // Oportunidades por separado — pueden tardar si el caché WS todavía no tiene datos
      axios.get(`${API}/arbitrage/opportunities`, { withCredentials: true, timeout: 25000 })
        .then(r => {
          setOpportunities(r.data.opportunities || []);
          setWsStatus(r.data.ws_status || 'ok');
        }).catch(() => setOpportunities([]));
    } catch (error) {
      setLoading(false);
    }
  };

  const loadOpportunities = async () => {
    try {
      const [oppsRes, histRes] = await Promise.all([
        axios.get(`${API}/arbitrage/opportunities`, { withCredentials: true, timeout: 10000 }),
        axios.get(`${API}/arbitrage/historial?limit=200`, { withCredentials: true, timeout: 8000 }).catch(() => ({ data: { historial: [] } })),
      ]);
      setOpportunities(oppsRes.data.opportunities || []);
      setWsStatus(oppsRes.data.ws_status || 'ok');
      setHistorial(histRes.data.historial || []);
    } catch (error) {
      console.error('Error loading opportunities:', error);
    }
  };

  const handleInitiateTransfer = (opp) => {
    setSelectedOpp(opp);
    const [base] = opp.symbol.split('/');
    setTransferForm({
      from_exchange: opp.buy_exchange,
      to_exchange: opp.sell_exchange,
      currency: base,
      amount: ''
    });
    setShowTransferDialog(true);
  };

  const handleTransfer = async (e) => {
    e.preventDefault();
    if (!selectedOpp) return;
    setTransferring(true);
    try {
      const params = new URLSearchParams({
        symbol: selectedOpp.symbol,
        buy_exchange: selectedOpp.buy_exchange,
        sell_exchange: selectedOpp.sell_exchange,
        amount_usd: transferForm.amount,
        paper_trading: paperMode,
        tipo: selectedOpp.tipo || 'futures_vs_futures',
      });
      const response = await axios.post(`${API}/arbitrage/execute?${params}`, {}, { withCredentials: true });
      const r = response.data;
      toast.success(`${r.paper_trading ? '[PAPER] ' : '[REAL] '}Arbitraje ejecutado: +$${r.ganancia_usd?.toFixed(2)} USD (${r.profit_percentage}%)`);
      setShowTransferDialog(false);
      setTransferForm({ from_exchange: '', to_exchange: '', currency: '', amount: '' });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al ejecutar arbitraje');
    } finally {
      setTransferring(false);
    }
  };

  const saveAutoConfig = async () => {
    try {
      const params = new URLSearchParams(autoConfig);
      await axios.post(`${API}/arbitrage/auto/config?${params}`, {}, { withCredentials: true });
      toast.success(`Modo automático ${autoConfig.activo ? 'activado' : 'desactivado'}`);
      setShowAutoConfig(false);
    } catch { toast.error('Error al guardar configuración'); }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-full">
          <div className="text-center"><div className="aegis-spinner mx-auto"></div><p className="text-[#A1A1AA] text-sm mt-3">Cargando...</p></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6" data-testid="arbitrage-manager-page">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-3xl font-bold text-white tracking-tight">Arbitraje entre Exchanges</h1>
            <p className="text-[#A1A1AA] mt-1">
              Diferencias de precio en tiempo real | {opportunities.length} oportunidades detectadas
            </p>
          </div>
          <div className="flex items-center gap-2">
            {/* Modo determinado automáticamente por el estado de inversión */}
            <div className="flex items-center gap-2 bg-[#1E1E1E] border border-[#27272A] rounded-lg px-3 py-2">
              <span className="text-xs text-[#A1A1AA]">Modo:</span>
              {investmentActive ? (
                <span className="px-2 py-0.5 rounded text-xs font-bold bg-[#FFD600] text-black">REAL</span>
              ) : (
                <span className="px-2 py-0.5 rounded text-xs font-bold bg-[#00D4FF] text-black">PAPER</span>
              )}
            </div>
            {/* Config automático */}
            <button onClick={() => setShowAutoConfig(true)}
              className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-bold border transition-all ${autoConfig.activo ? 'bg-[#00FF88]/10 border-[#00FF88]/30 text-[#00FF88]' : 'bg-[#1E1E1E] border-[#27272A] text-[#A1A1AA]'}`}>
              <Zap className="w-3.5 h-3.5" />
              Auto {autoConfig.activo ? 'ON' : 'OFF'}
            </button>
          </div>
        </div>

        {exchanges.length < 2 && (
          <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-sm p-4">
            <div className="flex items-center gap-3">
              <AlertTriangle className="text-yellow-500" size={20} />
              <p className="text-yellow-500">
                Necesitas al menos 2 exchanges conectados para detectar oportunidades de arbitraje.
              </p>
            </div>
          </div>
        )}

        {opportunities.length === 0 ? (
          <div className="bg-[#121212] border border-[#27272A] rounded-sm p-12 text-center">
            <p className="text-[#A1A1AA]">
              {exchanges.length < 2
                ? 'Conecta más exchanges en Configuración para encontrar oportunidades'
                : wsStatus === 'conectando'
                  ? 'Conectando WebSocket... los precios aparecerán en segundos'
                  : 'Sin oportunidades rentables en este momento'}
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            {opportunities.map((opp, idx) => (
              <div key={idx}
                className={`bg-[#121212] border rounded-sm p-4 transition-colors ${opp.profitable ? 'border-[#00FF88]/30 hover:border-[#00FF88]/60' : 'border-[#27272A] hover:border-[#3F3F46]'}`}>
                <div className="flex items-center gap-4">
                  {/* Symbol + tipo */}
                  <div className="w-28">
                    <p className="text-base font-bold text-white mono">{opp.symbol}</p>
                    {opp.profitable && <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#00FF88]/15 text-[#00FF88] font-bold">RENTABLE</span>}
                    {opp.tipo && (
                      <span className={`block mt-0.5 text-[10px] px-1.5 py-0.5 rounded font-bold ${
                        opp.tipo === 'futures_vs_futures'
                          ? 'bg-purple-500/15 text-purple-400'
                          : 'bg-blue-500/15 text-blue-400'
                      }`}>
                        {opp.tipo === 'futures_vs_futures' ? '⚡ Fut vs Fut' : '📊 Spot vs Fut'}
                      </span>
                    )}
                  </div>

                  {/* Comprar */}
                  <div className="flex-1">
                    <p className="text-[10px] text-[#A1A1AA] uppercase">Comprar en</p>
                    <p className="text-sm text-white font-medium">{opp.buy_exchange}</p>
                    <p className="text-[10px] text-[#52525B] uppercase">{opp.buy_market || 'spot'}</p>
                    <p className="text-sm mono text-[#00FF88]">${opp.buy_price < 1 ? opp.buy_price.toFixed(8) : opp.buy_price.toFixed(2)}</p>
                    <p className="text-[10px] text-[#52525B]">Fee: {opp.fee_buy_pct}%</p>
                  </div>

                  {/* Flecha */}
                  <ArrowRight className="text-[#00D4FF] shrink-0" size={20} />

                  {/* Vender */}
                  <div className="flex-1">
                    <p className="text-[10px] text-[#A1A1AA] uppercase">Vender en</p>
                    <p className="text-sm text-white font-medium">{opp.sell_exchange}</p>
                    <p className="text-[10px] text-[#52525B] uppercase">{opp.sell_market || 'futures'}</p>
                    <p className="text-sm mono text-[#FF3B30]">${opp.sell_price < 1 ? opp.sell_price.toFixed(8) : opp.sell_price.toFixed(2)}</p>
                    <p className="text-[10px] text-[#52525B]">Fee: {opp.fee_sell_pct}%</p>
                  </div>

                  {/* Spread */}
                  <div className="text-center w-28">
                    <p className={`text-xl font-bold mono ${opp.profit_percentage >= 0 ? 'text-[#00FF88]' : 'text-[#FF3B30]'}`}>
                      {opp.profit_percentage >= 0 ? '+' : ''}{opp.profit_percentage}%
                    </p>
                    <p className="text-[10px] text-[#52525B]">Bruto {opp.gross_percentage}% · Fees {opp.fees_percentage}%</p>
                    <p className="text-[10px] text-[#52525B]">Red ~${opp.network_fee_usd}</p>
                  </div>

                  {/* Rentabilidad */}
                  <div className="text-right w-32">
                    {opp.profitable ? (
                      <>
                        <p className="text-[10px] text-[#A1A1AA]">Monto mínimo rentable</p>
                        <p className="text-sm font-bold text-[#00FF88] mono">${(opp.min_amount_usd || 0).toLocaleString()}</p>
                        <button onClick={() => handleInitiateTransfer(opp)}
                          className="mt-1 w-full py-1.5 rounded-lg text-xs font-bold bg-gradient-to-r from-[#00CC77] to-[#00FF88] text-black">
                          Ejecutar
                        </button>
                      </>
                    ) : (
                      <p className="text-xs text-[#52525B]">No rentable ahora</p>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Dialog de Arbitraje */}
        <Dialog open={showTransferDialog} onOpenChange={setShowTransferDialog}>
          <DialogContent className="bg-[#121212] border-[#27272A] text-white max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Ejecutar Arbitraje</DialogTitle>
            </DialogHeader>

            {selectedOpp && (() => {
              const netPct = selectedOpp.profit_percentage / 100;
              const netFee = selectedOpp.network_fee_usd || 0;
              const minUsd = selectedOpp.min_amount_usd || 100;
              const amt = parseFloat(transferForm.amount) || 0;
              const ganBruta = amt * (selectedOpp.gross_percentage / 100);
              const feesTrading = amt * (selectedOpp.fees_percentage / 100);
              const ganNeta = ganBruta - feesTrading - netFee;
              const samples = [minUsd, 200, 500, 1000, 2000, 5000].filter(v => v >= minUsd);
              if (!samples.includes(minUsd)) samples.unshift(minUsd);

              return (
                <div className="space-y-4">
                  <div className="bg-[#1E1E1E] p-3 rounded border border-[#27272A]">
                    <div className="flex justify-between items-center">
                      <div>
                        <p className="text-lg font-bold text-white mono">{selectedOpp.symbol}</p>
                        <p className="text-xs text-[#A1A1AA]">Comprar en {selectedOpp.buy_exchange} a ${selectedOpp.buy_price < 1 ? selectedOpp.buy_price.toFixed(8) : selectedOpp.buy_price.toFixed(2)}</p>
                        <p className="text-xs text-[#A1A1AA]">Vender en {selectedOpp.sell_exchange} a ${selectedOpp.sell_price < 1 ? selectedOpp.sell_price.toFixed(8) : selectedOpp.sell_price.toFixed(2)}</p>
                      </div>
                      <p className={`text-2xl font-bold mono ${selectedOpp.profit_percentage >= 0 ? 'text-[#00FF88]' : 'text-[#FF3B30]'}`}>
                        {selectedOpp.profit_percentage >= 0 ? '+' : ''}{selectedOpp.profit_percentage}%
                      </p>
                    </div>
                  </div>

                  <div>
                    <p className="text-xs text-[#A1A1AA] mb-2 font-medium">Simulación de ganancia neta (después de todos los fees)</p>
                    <div className="border border-[#27272A] rounded overflow-hidden text-xs">
                      <div className="grid grid-cols-4 bg-[#1E1E1E] text-[#A1A1AA] font-medium">
                        <div className="p-2">Inviertes</div>
                        <div className="p-2 text-right">Bruto</div>
                        <div className="p-2 text-right">Fees</div>
                        <div className="p-2 text-right">Ganancia neta</div>
                      </div>
                      {samples.map(s => {
                        const gb = s * (selectedOpp.gross_percentage / 100);
                        const ft = s * (selectedOpp.fees_percentage / 100);
                        const gn = gb - ft - netFee;
                        return (
                          <div key={s} className="grid grid-cols-4 border-t border-[#27272A]/50">
                            <div className="p-2 text-white mono">${s.toLocaleString()}</div>
                            <div className="p-2 text-right text-[#A1A1AA] mono">${gb.toFixed(2)}</div>
                            <div className="p-2 text-right text-red-400 mono">-${(ft + netFee).toFixed(2)}</div>
                            <div className={`p-2 text-right font-bold mono ${gn > 0 ? 'text-[#00FF88]' : 'text-[#FF3B30]'}`}>
                              ${gn.toFixed(2)}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    <p className="text-[10px] text-[#52525B] mt-1">Fees trading: {selectedOpp.fee_buy_pct}% + {selectedOpp.fee_sell_pct}% · Fee de red: ~${netFee}</p>
                  </div>

                  <form onSubmit={handleTransfer} className="space-y-3">
                    <div>
                      <Label className="text-xs">Tu monto (USDT)</Label>
                      <Input type="number" step="any" min={minUsd}
                        value={transferForm.amount}
                        onChange={(e) => setTransferForm({...transferForm, amount: e.target.value})}
                        placeholder={`Mínimo $${minUsd}`} required />
                    </div>

                    {amt > 0 && (
                      <div className={`p-3 rounded border ${ganNeta > 0 ? 'bg-[#00FF88]/10 border-[#00FF88]/30' : 'bg-red-500/10 border-red-500/30'}`}>
                        <div className="flex justify-between text-sm">
                          <span className="text-[#A1A1AA]">Ganancia bruta:</span>
                          <span className="text-white mono">${ganBruta.toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-[#A1A1AA]">Fees trading:</span>
                          <span className="text-red-400 mono">-${feesTrading.toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-[#A1A1AA]">Fee de red:</span>
                          <span className="text-red-400 mono">-${netFee.toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between text-sm font-bold border-t border-[#27272A] pt-1 mt-1">
                          <span className="text-white">Ganancia neta:</span>
                          <span className={`mono ${ganNeta > 0 ? 'text-[#00FF88]' : 'text-[#FF3B30]'}`}>${ganNeta.toFixed(2)}</span>
                        </div>
                      </div>
                    )}

                    <div className="flex gap-3">
                      <Button type="button" onClick={() => setShowTransferDialog(false)}
                        variant="outline" className="flex-1" disabled={transferring}>
                        Cancelar
                      </Button>
                      <button type="submit" disabled={transferring || ganNeta <= 0}
                        className={`flex-1 py-2 rounded-lg text-sm font-bold transition-all ${ganNeta > 0
                          ? 'bg-gradient-to-r from-[#00CC77] to-[#00FF88] text-black'
                          : 'bg-[#27272A] text-[#52525B] cursor-not-allowed'}`}>
                        {transferring ? 'Ejecutando...' : ganNeta > 0 ? `Ejecutar (+$${ganNeta.toFixed(2)})` : 'No rentable con este monto'}
                      </button>
                    </div>
                  </form>
                </div>
              );
            })()}
          </DialogContent>
        </Dialog>

        {/* Saldo paper global */}
        {paperBal && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="bg-[#121212] border border-[#27272A] rounded-sm p-3">
              <p className="text-[10px] text-[#A1A1AA] uppercase tracking-wider">Saldo Paper Total</p>
              <p className="text-xl font-bold text-[#00D4FF] mono mt-0.5">${paperBal.saldo_actual.toFixed(2)}</p>
              <p className="text-[10px] text-[#52525B]">Bots + Manual + Arbitraje</p>
            </div>
            <div className="bg-[#121212] border border-[#27272A] rounded-sm p-3">
              <p className="text-[10px] text-[#A1A1AA] uppercase tracking-wider">P&L Acumulado</p>
              <p className={`text-xl font-bold mono mt-0.5 ${paperBal.pnl_acumulado >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                {paperBal.pnl_acumulado >= 0 ? '+' : ''}${paperBal.pnl_acumulado.toFixed(2)}
              </p>
              <p className="text-[10px] text-[#52525B]">Sobre $10,000 inicial</p>
            </div>
            {(() => {
              const arbPaper = arbFull.filter(h => h.paper_trading);
              const arbReal  = arbFull.filter(h => !h.paper_trading);
              const sumP = arbPaper.reduce((s, h) => s + (h.ganancia_usd || 0), 0);
              const sumR = arbReal.reduce((s, h) => s + (h.ganancia_usd || 0), 0);
              return (
                <>
                  <div className="bg-[#121212] border border-[#27272A] rounded-sm p-3">
                    <p className="text-[10px] text-[#A1A1AA] uppercase tracking-wider">Arbitraje Paper</p>
                    <p className={`text-xl font-bold mono mt-0.5 ${sumP >= 0 ? 'text-[#00D4FF]' : 'text-red-400'}`}>
                      {sumP >= 0 ? '+' : ''}${sumP.toFixed(4)}
                    </p>
                    <p className="text-[10px] text-[#52525B]">{arbPaper.length} operaciones</p>
                  </div>
                  <div className="bg-[#121212] border border-[#27272A] rounded-sm p-3">
                    <p className="text-[10px] text-[#A1A1AA] uppercase tracking-wider">Arbitraje Real</p>
                    <p className={`text-xl font-bold mono mt-0.5 ${sumR >= 0 ? 'text-[#FFD600]' : 'text-red-400'}`}>
                      {sumR >= 0 ? '+' : ''}${sumR.toFixed(4)}
                    </p>
                    <p className="text-[10px] text-[#52525B]">{arbReal.length} operaciones</p>
                  </div>
                </>
              );
            })()}
          </div>
        )}

        {/* Posiciones abiertas con P&L en tiempo real */}
        {/* Historial de arbitrajes */}
        <div className="bg-[#121212] border border-[#27272A] rounded-sm p-4">
          <h3 className="text-white font-bold mb-3 text-sm">
            Historial de Arbitrajes
            {historial.length > 0 && <span className="ml-2 text-[10px] text-[#52525B] font-normal">({historial.length} últimas)</span>}
          </h3>

          {/* Gráfico de equity acumulada paper vs real — sobre el histórico
              completo (arbFull), no solo las últimas 200 de la tabla */}
          {arbFull.length > 0 && (() => {
            const sorted = [...arbFull].sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
            let paperCum = 0, realCum = 0;
            const chartData = sorted.map(h => {
              if (h.paper_trading) paperCum = Number((paperCum + (h.ganancia_usd || 0)).toFixed(4));
              else realCum = Number((realCum + (h.ganancia_usd || 0)).toFixed(4));
              return {
                fecha: new Date(h.timestamp).toLocaleTimeString('es', { hour: '2-digit', minute: '2-digit' }),
                paper: h.paper_trading ? paperCum : undefined,
                real: !h.paper_trading ? realCum : undefined,
              };
            });
            const totalPaper = Number(paperCum.toFixed(4));
            const totalReal  = Number(realCum.toFixed(4));
            return (
              <div className="mb-4">
                <div className="flex gap-4 mb-2 text-xs">
                  {totalPaper !== 0 && (
                    <span className="flex items-center gap-1">
                      <span className="inline-block w-2.5 h-2.5 rounded-full bg-[#00D4FF]" />
                      <span className="text-[#A1A1AA]">Paper acum:</span>
                      <span className={`font-bold mono ${totalPaper >= 0 ? 'text-[#00D4FF]' : 'text-red-400'}`}>
                        ${totalPaper >= 0 ? '+' : ''}{totalPaper}
                      </span>
                    </span>
                  )}
                  {totalReal !== 0 && (
                    <span className="flex items-center gap-1">
                      <span className="inline-block w-2.5 h-2.5 rounded-full bg-[#FFD600]" />
                      <span className="text-[#A1A1AA]">Real acum:</span>
                      <span className={`font-bold mono ${totalReal >= 0 ? 'text-[#FFD600]' : 'text-red-400'}`}>
                        ${totalReal >= 0 ? '+' : ''}{totalReal}
                      </span>
                    </span>
                  )}
                </div>
                <ResponsiveContainer width="100%" height={140}>
                  <LineChart data={chartData} margin={{ top: 4, right: 8, bottom: 0, left: 8 }}>
                    <XAxis dataKey="fecha" tick={{ fill: '#52525B', fontSize: 10 }} />
                    <YAxis tick={{ fill: '#52525B', fontSize: 10 }} width={48} />
                    <Tooltip
                      contentStyle={{ backgroundColor: '#1E1E1E', border: '1px solid #27272A', borderRadius: 4, fontSize: 11 }}
                      formatter={(val, name) => val != null ? [`$${Number(val).toFixed(4)}`, name === 'paper' ? 'Paper' : 'Real'] : null}
                    />
                    {chartData.some(d => d.paper !== undefined) && (
                      <Line type="monotone" dataKey="paper" stroke="#00D4FF" strokeWidth={2} dot={false} activeDot={{ r: 4 }} name="paper" connectNulls />
                    )}
                    {chartData.some(d => d.real !== undefined) && (
                      <Line type="monotone" dataKey="real" stroke="#FFD600" strokeWidth={2} dot={false} activeDot={{ r: 4 }} name="real" connectNulls />
                    )}
                  </LineChart>
                </ResponsiveContainer>
                <p className="text-[10px] text-[#52525B] mt-1">Azul = Paper · Amarillo = Real · Ganancia acumulada USD</p>
              </div>
            );
          })()}

          {historial.length === 0 ? (
            <p className="text-[#52525B] text-xs">Sin ejecuciones aún. Activa el modo automático o ejecuta una oportunidad manualmente.</p>
          ) : (
            <>
              <p className="text-[10px] text-[#52525B] mb-2">{historial.length} operaciones registradas</p>
              <div className="space-y-2 max-h-[340px] overflow-y-auto pr-1 scrollbar-thin scrollbar-track-transparent scrollbar-thumb-[#27272A]">
                {historial.map((h, i) => (
                  <div key={i} className="flex items-center justify-between gap-2 text-xs border-b border-[#27272A]/30 pb-2">
                    <span className="text-[#A1A1AA] shrink-0">{new Date(h.timestamp).toLocaleString()}</span>
                    <span className="text-white mono">{h.symbol}</span>
                    {h.tipo && (
                      <span className={`px-1.5 py-0.5 rounded text-[10px] shrink-0 ${
                        h.tipo === 'futures_vs_futures' ? 'bg-purple-500/15 text-purple-400' : 'bg-blue-500/15 text-blue-400'
                      }`}>
                        {h.tipo === 'futures_vs_futures' ? 'F vs F' : 'S vs F'}
                      </span>
                    )}
                    <span className="text-[#52525B]">{h.buy_exchange} → {h.sell_exchange}</span>
                    <span className={`font-bold mono ${h.ganancia_usd >= 0 ? 'text-[#00FF88]' : 'text-[#FF3B30]'}`}>
                      +${h.ganancia_usd?.toFixed(2)}
                    </span>
                    <span className={`px-1.5 py-0.5 rounded text-[10px] shrink-0 ${h.paper_trading ? 'bg-[#00D4FF]/10 text-[#00D4FF]' : 'bg-[#FFD600]/10 text-[#FFD600]'}`}>
                      {h.paper_trading ? 'PAPER' : 'REAL'}
                    </span>
                    <span className={`px-1.5 py-0.5 rounded text-[10px] shrink-0 ${h.status === 'executed' || h.status === 'simulated' ? 'bg-[#00FF88]/10 text-[#00FF88]' : 'bg-red-500/10 text-red-400'}`}>
                      {h.status}
                    </span>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        {/* Modal configuración automática */}
        {showAutoConfig && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60" onClick={() => setShowAutoConfig(false)}>
            <div className="bg-[#18181B] border border-[#27272A] rounded-xl p-6 w-full max-w-sm" onClick={e => e.stopPropagation()}>
              <h3 className="text-white font-bold mb-4 flex items-center gap-2"><Zap className="w-4 h-4 text-[#00FF88]" /> Arbitraje Automático</h3>
              <p className="text-[#A1A1AA] text-xs mb-4">Cuando detecte una oportunidad que supere el umbral, ejecutará automáticamente.</p>
              <div className="space-y-3">
                <label className="flex items-center justify-between">
                  <span className="text-sm text-white">Activar modo automático</span>
                  <button onClick={() => setAutoConfig({...autoConfig, activo: !autoConfig.activo})}
                    className={`w-10 h-5 rounded-full transition-all ${autoConfig.activo ? 'bg-[#00FF88]' : 'bg-[#27272A]'}`}>
                    <div className={`w-4 h-4 rounded-full bg-white mx-0.5 transition-all ${autoConfig.activo ? 'translate-x-5' : ''}`} />
                  </button>
                </label>
                <div>
                  <label className="text-xs text-[#A1A1AA]">Umbral mínimo de ganancia (%)<span className="ml-1 text-[#52525B]">— recomendado: 0.03–0.10%</span></label>
                  <input type="number" step="0.01" min="0.01" value={autoConfig.umbral_pct}
                    onChange={e => setAutoConfig({...autoConfig, umbral_pct: parseFloat(e.target.value)})}
                    className="w-full mt-1 bg-[#09090B] border border-[#27272A] text-white rounded px-3 py-1.5 text-sm" />
                </div>
                <div>
                  <label className="text-xs text-[#A1A1AA]">Capital máximo por operación (USD)</label>
                  <input type="number" step="10" min="10" value={autoConfig.capital_max_usd}
                    onChange={e => setAutoConfig({...autoConfig, capital_max_usd: parseFloat(e.target.value)})}
                    className="w-full mt-1 bg-[#09090B] border border-[#27272A] text-white rounded px-3 py-1.5 text-sm" />
                </div>
                <label className="flex items-center justify-between pt-1 border-t border-[#27272A]">
                  <span className="text-sm text-white">Arbitraje unilateral</span>
                  <button onClick={() => setAutoConfig({...autoConfig, modo_unilateral: !autoConfig.modo_unilateral})}
                    className={`w-10 h-5 rounded-full transition-all ${autoConfig.modo_unilateral ? 'bg-[#00FF88]' : 'bg-[#27272A]'}`}>
                    <div className={`w-4 h-4 rounded-full bg-white mx-0.5 transition-all ${autoConfig.modo_unilateral ? 'translate-x-5' : ''}`} />
                  </button>
                </label>
                <p className="text-[10px] text-[#52525B] -mt-2">
                  Cuando solo una de tus exchanges tiene fondos reales, opera solo ahí (usando la otra únicamente como referencia de precio) en vez de intentar las dos y arriesgar una posición sin cobertura.
                </p>
                <div className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-semibold ${investmentActive ? 'bg-[#FFD600]/10 text-[#FFD600] border border-[#FFD600]/30' : 'bg-[#00D4FF]/10 text-[#00D4FF] border border-[#00D4FF]/30'}`}>
                  {investmentActive ? '⚡ Modo REAL (inversión activa)' : '📋 Modo PAPER (sin inversión activa)'}
                </div>
              </div>
              <div className="flex gap-3 mt-5">
                <button onClick={() => setShowAutoConfig(false)} className="flex-1 py-2 rounded-lg text-sm border border-[#27272A] text-[#A1A1AA]">Cancelar</button>
                <button onClick={saveAutoConfig} className="flex-1 py-2 rounded-lg text-sm font-bold bg-[#00FF88] text-black">Guardar</button>
              </div>
            </div>
          </div>
        )}

        {/* Info adicional */}
        <div className="bg-[#1E1E1E] border border-[#27272A] rounded-sm p-6">
          <h3 className="text-white font-bold mb-3">Cómo Funciona el Arbitraje</h3>
          <div className="space-y-2 text-sm text-[#A1A1AA]">
            <p>1. Necesitas saldo en AMBOS exchanges antes de operar</p>
            <p>2. Se compra en el exchange más barato y se vende en el más caro simultáneamente</p>
            <p>3. La ganancia es la diferencia de precio menos las comisiones de trading</p>
            <p className="text-yellow-500 mt-4">
              ⚠️ El precio se re-confirma justo antes de ejecutar. Si ya no es rentable, se cancela automáticamente.
            </p>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default ArbitrageManager;